/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {
  ConnectionEvent,
  ConnectionState,
  ErrorCode,
  IRemoteTrack,
  MediaType,
  NAME,
  PublishedUser,
  RemoteAudioTrack,
  RemoteAuxiliaryTrack,
  RemoteStreamType,
  RemoteVideoTrack,
  RoomEvent,
  RtcError,
  SSRC,
  UserRole,
  VideoCodec,
  RemoteTrackEvent
} from 'trtc-js-sdk-core';
import { ErrorName } from 'trtc-js-sdk-core/src/common/error-info';
import { generateErrorMessage } from 'trtc-js-sdk-core/src/common/error-generator';
import { getMuteStateFromFlag, isNumber } from 'trtc-js-sdk-core/src/utils/utils';
import { RemoteTracksType, SUBSCRIBE_TYPES } from '../../type/type';
import { SignalCommand, SignalEvent } from '../../signal/constant';
import ConnectionSPC from './connection-spc';
import { SPC_EVENT, buildSSRCGroup, getNewSSRC } from './single-peerconnection';
import { createDecorator } from 'trtc-js-sdk-core/src/utils/decorators/middleware';
import { addCallQueue, addCallQueueValidArgs } from 'trtc-js-sdk-core/src/utils/decorators/api-call-queue';
import { CHROME_MAJOR_VERSION, IS_CHROMIUM_BASE } from 'trtc-js-sdk-core/src/utils/environment';
import { isUndefined } from 'trtc-js-sdk-core/src/utils/type-check';
import { DownlinkConnectionOption } from '../type';
import { IS_JITTER_BUFFER_TARGET_SUPPORTED } from 'trtc-js-sdk-core/src/common/rtc-detection';
import blackVideoDetector from 'trtc-js-sdk-core/src/utils/black-video-detector';
import kvStatManager, { KV_REPORT_KEY_SMALL_MODE } from 'trtc-js-sdk-core/src/manager/kv-stat-manager';
import AVPlayerStateSyncManager from 'trtc-js-sdk-core/src/common/player/av-player-state-sync-manager';

export function getSubscribedKeys(subscribeState: SubscribeState) {
  return Object.keys(subscribeState).filter(key => subscribeState[key as keyof typeof subscribeState]);
}

/**
 * // FIXME: 下行重连过程中，会频繁触发 track mute unmute
 * // TODO: 下行重连过程中，减少日志打印。
 * 继承自 Connection 类，实现下行重连逻辑
 * 将下行相关功能逻辑从 Connection 中分离
 * @class DownlinkConnection
 * @extends {Connection}
 */
class DownlinkConnectionSPC extends ConnectionSPC implements PublishedUser {
  private _flag = 0;
  isRobot = false;
  role = UserRole.ANCHOR;
  fromType?: number;
  remoteAudioTrack: RemoteAudioTrack;
  remoteVideoTrack: RemoteVideoTrack;
  remoteAuxiliaryTrack: RemoteAuxiliaryTrack;
  // 记录下行流的 ssrc，可在上报 monitor 时，根据 ssrc 来区分数据类型
  ssrc: SSRC = {
    audio: 0,
    video: 0,
    videoRtx: 0,
    auxiliary: 0,
    auxiliaryRtx: 0
  };
  // 记录之前使用的 mid，用于重新订阅时复用，避免 mid 变化导致 track 发生变化
  private _prevMids?: number[];

  jitterBufferTimeoutId = -1;
  private _jitterBufferResolve?: (value?: unknown) => void; // 保存当前 jitterBuffer 更新的 resolve 函数
  private _videoCodec?: VideoCodec;

  avPlayerStateSyncManager: AVPlayerStateSyncManager;
  private isDataChannelSubscribed = false;
  get videoCodec() {
    // H265 "CIT01_49_level-id=180;profile-id=1;tier-flag=0;tx-mode=SRST"
    // H264 "CIT01_109_level-asymmetry-allowed=1;packetization-mode=1;profile-level-id=42e01f"
    return this._videoCodec || this.singlePC?.downlinkVideoCodec || VideoCodec.H264;
  }
  set videoCodec(value: VideoCodec) {
    // 通过 stats 获取实际的 codec 信息
    this._videoCodec = value;
  }
  constructor(options: DownlinkConnectionOption) {
    super({ ...options, isUplink: false });
    // 远端用户的推流状态
    // 用于标识远端用户是否有推 audio, video, aux 流
    // this.muteState = options.muteState;
    this.flag = options.flag;
    this.isRobot = options.isRobot || false;
    this.fromType = options.fromType;
    this.remoteAudioTrack = new RemoteAudioTrack(this._room, this);
    this.remoteVideoTrack = new RemoteVideoTrack(this._room, this);
    this.remoteAuxiliaryTrack = new RemoteAuxiliaryTrack(this._room, this);
    this.avPlayerStateSyncManager = new AVPlayerStateSyncManager({
      log: this._log,
      audioPlayer: this.remoteAudioTrack.player,
      videoPlayer: this.remoteVideoTrack.player
    });
    this.initialize();
  }

  get subscribeState(): SubscribeState {
    return {
      audio: this.remoteAudioTrack.isSubscribed || this.remoteAudioTrack.isSubscribing,
      video: this.remoteVideoTrack.isBig && (this.remoteVideoTrack.isSubscribed || this.remoteVideoTrack.isSubscribing),
      smallVideo: this.remoteVideoTrack.isSmall && (this.remoteVideoTrack.isSubscribed || this.remoteVideoTrack.isSubscribing),
      auxiliary: this.remoteAuxiliaryTrack.isSubscribed || this.remoteAuxiliaryTrack.isSubscribing,
      datachannel: this.isDataChannelSubscribed
    };
  }

  get muteState() {
    return getMuteStateFromFlag(this.flag, this.userId);
  }

  get flag() {
    return this._flag;
  }

  set flag(value: number) {
    if (value === this._flag) return;
    this._flag = value;
    this.remoteAudioTrack?.onFlagChanged();
    this.remoteVideoTrack?.onFlagChanged();
    this.remoteAuxiliaryTrack?.onFlagChanged();
  }

  // 当前是否有主流
  get hasMainStream() {
    return this.muteState.hasAudio || this.muteState.hasVideo || this.muteState.hasSmall;
  }

  // 当前是否有辅流
  get hasAuxStream() {
    return this.muteState.hasAuxiliary;
  }

  /**
   * 判断当前是否订阅了主流
   */
  get isMainStreamSubscribed() {
    return (
      (this.subscribeState.audio ||
        this.subscribeState.video ||
        this.subscribeState.smallVideo) &&
      (this.muteState.hasAudio || this.muteState.hasVideo || this.muteState.hasSmall)
    );
  }

  /**
   * 判断当前是否订阅了辅流
   */
  get isAuxStreamSubscribed() {
    return this.subscribeState.auxiliary && this.muteState.hasAuxiliary;
  }

  /**
   * 判断当前是否订阅了小流
   */
  get isSmallStreamSubscribed() {
    return this.subscribeState.smallVideo && this.muteState.hasSmall;
  }

  /**
   * 判断当前是否订阅了大流
   */
  get isBigStreamSubscribed() {
    return this.subscribeState.video && this.muteState.hasVideo;
  }

  isStreamUnpublished(streamType: RemoteStreamType) {
    if (streamType === NAME.MAIN) {
      return !this.muteState.hasAudio && !this.muteState.hasVideo;
    }
    return !this.muteState.hasAuxiliary;
  }

  initialize() {
    this.installEvents();
  }

  close(reason?: string) {
    clearTimeout(this.jitterBufferTimeoutId);
    super.close(reason);
    // 远端取消推流，将远端推流状态设为 false
    this.emitConnectionStateChangedEvent(ConnectionState.DISCONNECTED);

    this.remoteAudioTrack.close();
    this.remoteVideoTrack.close();
    this.remoteAuxiliaryTrack.close();
    this.avPlayerStateSyncManager.destroy();
    this.uninstallEvents();
    this.removeDownlink();
  }

  /**
   *
   * @private
   * @memberof Connection
   */
  installEvents() {
    if (!this.singlePC) return;
    if (!this.listeners(SPC_EVENT.TRACK).includes(this.onTrack)) {
      this.singlePC.on(SPC_EVENT.TRACK, this.onTrack, this);
    }
    if (!this.listeners(SPC_EVENT.RECONNECTED).includes(this.onSinglePCReconnected)) {
      this.singlePC.on(SPC_EVENT.RECONNECTED, this.onSinglePCReconnected, this);
    }
    this.remoteVideoTrack.on(RemoteTrackEvent.DECODE_FAILED, this.onDecodeFailed, this);
  }

  uninstallEvents() {
    if (!this.singlePC) return;
    this.singlePC.off(SPC_EVENT.TRACK, this.onTrack, this);
    this.singlePC.off(SPC_EVENT.RECONNECTED, this.onSinglePCReconnected, this);
    this.remoteVideoTrack.off(RemoteTrackEvent.DECODE_FAILED, this.onDecodeFailed, this);
  }

  emitConnectionStateChangedEvent(state: ConnectionState) {
    const prevState = this._currentState;
    const result = super.emitConnectionStateChangedEvent(state);
    if (result && prevState !== state) {
      this.remoteVideoTrack?.emit(ConnectionEvent.CONNECTION_STATE_CHANGED, { prevState, state });
      this.remoteAuxiliaryTrack?.emit(ConnectionEvent.CONNECTION_STATE_CHANGED, { prevState, state });
    }
    return result;
  }

  /**
   * RTCPeerConnection.ontrack handler
   *
   * @private
   * @param {*} event
   * @memberof Connection
   */
  onTrack(event: RTCTrackEvent) {
    const stream = event.streams[0];
    const { track, receiver } = event;
    if (!stream.id.includes(this.tinyId)) return;
    const isAux = stream.id.includes('aux');
    const streamType = isAux ? RemoteStreamType.Aux : RemoteStreamType.Main;

    this._log.debug(`ontrack ${streamType} ${track.kind}`);
    let remoteTrackType: keyof RemoteTracksType = NAME.AUDIO;
    if (track.kind === NAME.VIDEO) {
      remoteTrackType = streamType === NAME.MAIN ? NAME.VIDEO : NAME.AUXILIARY;
    }
    let remoteTrack: IRemoteTrack = this.remoteAudioTrack;
    if (remoteTrackType === NAME.VIDEO) {
      remoteTrack = this.remoteVideoTrack;
    } else if (remoteTrackType === NAME.AUXILIARY) {
      remoteTrack = this.remoteAuxiliaryTrack;
    }
    this.singlePC?.receiverRemoteTrackMap.set(receiver, remoteTrack);
    if (this.singlePC?.scriptTransformWorker) {
      this.initReceiverTransform(receiver, streamType, track.kind === NAME.AUDIO);
    }
    if (this.singlePC!.enableInsertableStreams) {
      this.createEncodedStreams(receiver);
    }
    remoteTrack.setInputMediaStreamTrack(track);
  }
  createEncodedStreams(receiver: RTCRtpReceiver) {
    if (!this.singlePC!.insertableStreamsAbortMap.has(receiver)) {
      // @ts-ignore
      const encodedStreams = receiver.createEncodedStreams();
      const abortController = new AbortController();
      const ctx = {
        abortController, enqueue: (chunk: RTCEncodedAudioFrame | RTCEncodedVideoFrame) => {
          const remoteTrack = this.singlePC?.receiverRemoteTrackMap.get(receiver);
          if (!remoteTrack || (remoteTrack.kind === 'video' && !this.singlePC?.isUsingH264 && !this.singlePC?.isUsingH265)) {
            return chunk;
          }
          return remoteTrack.decodeFrame(chunk);
        }
      };
      encodedStreams.readable
        .pipeThrough(new TransformStream({
          transform: (chunk: RTCEncodedAudioFrame | RTCEncodedVideoFrame, controller) => {
            const transformed = ctx.enqueue(chunk);
            if (transformed) controller.enqueue(transformed);
          }
        })).pipeTo(encodedStreams.writable, abortController)
        .catch((error: any) => {
          if (error !== 'destroy') this._log.warn(error);
        });
      this.singlePC!.addAbortController(receiver, abortController);
    }
  }

  initReceiverTransform(receiver: RTCRtpReceiver, streamType: RemoteStreamType, isAudio: boolean) {
    if (!this._peerConnection || !this.singlePC || !this.singlePC.scriptTransformWorker) return;
    if (!receiver.transform) {
      receiver.transform = new RTCRtpScriptTransform(this.singlePC.scriptTransformWorker, { isReceiver: true, isAudio, userId: this.userId, streamType });
    }
  }

  @addCallQueue()
  // 订阅过程中，远端取消推流，subscribe 可能会一直处于 pending 状态，此时需及时 reject。
  @createDecorator<DownlinkConnectionSPC>(next => function (this: DownlinkConnectionSPC, ...args) {
    return new Promise((resolve, reject) => {
      const onClosed = (reason: string) => {
        this.off(ConnectionEvent.CLOSED, onClosed);
        reject(new RtcError({
          code: ErrorCode.API_CALL_ABORTED,
          message: generateErrorMessage({
            key: ErrorName.CONNECTION_ABORTED,
            data: reason
          })
        }));
      };
      this.on(ConnectionEvent.CLOSED, onClosed);
      next.apply(this, args).then(resolve, reject)
        .finally(() => {
          this.off(ConnectionEvent.CLOSED, onClosed);
        });
    });
  })
  async subscribe(subscribeState: SubscribeState, streamType: RemoteStreamType) {
    try {
      let isNeedToGetNewSSRC = true;
      this._log.info(`subscribe ${streamType} ${getSubscribedKeys(subscribeState)}`);
      // 已有 ssrc，订阅状态变更。
      if (this.hasSSRC) {
        let commandType = SUBSCRIBE_TYPES.SUBSCRIBE_CHANGE;
        // 订阅状态变更，如果 audio, video, aux 都为 false, 则发 unsubscribe 信令
        if (!Object.values(subscribeState).find(item => item === true)) {
          commandType = SUBSCRIBE_TYPES.UNSUBSCRIBE;
        }
        await this.sendSubscription(commandType, subscribeState);
      } else {
        // 等待后台下发 cmd 4161 切换房间自动订阅结果响应, 避免使用新生成的 ssrcGroup 订阅导致黑屏 ref: https://tapd.woa.com/tapd_fe/20396022/story/detail/1020396022126413876
        await this._room.switchRoomSubedReq;
        if (this.singlePC?.autoSubscribedUserMap.size) {
          const subscribedUser = this.singlePC.autoSubscribedUserMap.get(this.userId);
          if (subscribedUser) {
            this.singlePC.autoSubscribedUserMap.delete(this.userId);
            const ssrcGroup = this.singlePC.autoSubscribedSsrcGroups.get(this._room.roomId)?.[subscribedUser.groupIndex];
            if (ssrcGroup) {
              this.ssrc = {
                audio: ssrcGroup.audioSsrc,
                video: ssrcGroup.bigVideoSsrc,
                videoRtx: ssrcGroup.bigVideoRtxSsrc,
                auxiliary: ssrcGroup.auxVideoSsrc,
                auxiliaryRtx: ssrcGroup.auxVideoRtxSsrc
              };
              isNeedToGetNewSSRC = false;
            }
          }
        }
        await this.doSubscribe(subscribeState, isNeedToGetNewSSRC);
        this.checkTrackEnded(subscribeState);
      }
      const { user, mediaTrack } = this.remoteVideoTrack;
      if (subscribeState.smallVideo && mediaTrack) {
        kvStatManager.addSuccessEvent({ key: KV_REPORT_KEY_SMALL_MODE.SWITCH_DOWNLINK_SMALL });

        this._blackSmallVideoDetectionId = blackVideoDetector.start({
          track: mediaTrack,
          isUplink: false,
          room: this._room,
          userId: this.userId,
          onBlack: () => {
            this._log.warn('small video is black, auto change to big');
            this._room.changeType(false, user);
            kvStatManager.addFailedEvent({ key: KV_REPORT_KEY_SMALL_MODE.SWITCH_DOWNLINK_SMALL });
            blackVideoDetector.stop(this._blackSmallVideoDetectionId);
            this._blackSmallVideoDetectionId = undefined;
          }
        });
      } else {
        blackVideoDetector.stop(this._blackSmallVideoDetectionId);
        this._blackSmallVideoDetectionId = undefined;
      }
    } catch (error: any) {
      // 远端取消推流导致的订阅失败。
      if (this._room.isJoined && this.isStreamUnpublished(streamType)) {
        this._log.warn(`${error.message} ${JSON.stringify(this.muteState)}`);
        throw new RtcError({
          code: ErrorCode.REMOTE_STREAM_NOT_EXIST,
          message: `remote user ${this.userId} unpublished stream`
        });
      }
      throw error;
    }
  }

  // iOS 14 13 在取消订阅后，会导致 audioTrack ended。此处在重新订阅之后，检测 track 状态，如有异常则重连 spc
  checkTrackEnded(subscribeState: SubscribeState) {
    if ((subscribeState.audio && this.remoteAudioTrack.mediaTrack?.readyState === 'ended') ||
      (subscribeState.video && this.remoteVideoTrack.mediaTrack?.readyState === 'ended') ||
      (subscribeState.auxiliary && this.remoteAuxiliaryTrack.mediaTrack?.readyState === 'ended')) {
      if (this.singlePC && !this.singlePC.isReconnecting) {
        this._log.warn('remote track ended start spc reconnect');
        // 小于 Chrome 92 版本遇到 remote track ended 不重连。
        // ref: https://tapd.woa.com/20396022/prong/stories/view/1020396022887925645
        if (IS_CHROMIUM_BASE && CHROME_MAJOR_VERSION < 92) return;
        this.singlePC.startReconnection();
      }
    }
  }

  /**
   * 取消订阅远端流
   */
  @addCallQueue()
  async unsubscribe({
    remoteTracks,
    streamType
  }: { remoteTracks: IRemoteTrack[]; streamType: RemoteStreamType; }) {
    if (((streamType === RemoteStreamType.Main && !this.isMainStreamSubscribed) ||
      (streamType === RemoteStreamType.Aux && !this.isAuxStreamSubscribed))) {
      // 避免重复取消订阅
      this._log.info(`${streamType} stream already unsubscribed`);
      return;
    }

    const subscribeState = { ...this.subscribeState };

    remoteTracks.forEach(remoteTrack => {
      switch (remoteTrack.mediaType) {
        case MediaType.AUDIO:
          subscribeState.audio = false;
          break;
        case MediaType.BIG_VIDEO:
          subscribeState.video = false;
          break;
        case MediaType.SMALL_VIDEO:
          subscribeState.smallVideo = false;
          break;
        case MediaType.AUX_VIDEO:
          subscribeState.auxiliary = false;
          break;
        default: break;
      }
    });

    let commandType = SUBSCRIBE_TYPES.SUBSCRIBE_CHANGE;
    // 当前还订阅着其他流，此时发 subscribe change 信令。
    // 例如：取消订阅主流，但是当前 peerConnection 还订阅着辅流，则发 subscribe change 信令。
    // 只有当 audio, video, aux 都不订阅时，发 unsubscribe 信令。
    if (!Object.values(subscribeState).find(item => item === true)) {
      commandType = SUBSCRIBE_TYPES.UNSUBSCRIBE;
    }
    this._log.info(`${commandType === SUBSCRIBE_TYPES.UNSUBSCRIBE ? commandType : 'subscribe'} ${streamType} [${getSubscribedKeys(subscribeState)}]`);

    if (commandType === SUBSCRIBE_TYPES.UNSUBSCRIBE) {
      this.singlePC?.removeDownlinkQueue.add(this.tinyId);
    }

    await this.sendSubscription(commandType, subscribeState);

    if (streamType === RemoteStreamType.Main) {
      blackVideoDetector.stop(this._blackSmallVideoDetectionId);
      this._blackSmallVideoDetectionId = undefined;
    }

    if (commandType === SUBSCRIBE_TYPES.UNSUBSCRIBE) {
      await this.removeDownlink();
    }
  }

  /**
   * 订阅 DataChannel（用于 transcriber）
   */
  async subscribeDataChannel() {
    if (!this.singlePC) return;
    await this.singlePC.waitForPeerConnectionConnected();

    const subscribeState = { ...this.subscribeState, datachannel: true };
    await this.doSubscribe(subscribeState);
  }

  /**
   * 取消订阅 DataChannel
   */
  async unsubscribeDataChannel() {
    const subscribeState = { ...this.subscribeState, datachannel: false };
    // 机器人场景下没有其他订阅位，取消 DataChannel 后，需要发送 unsubscribe 信令。
    await this.sendSubscription(SUBSCRIBE_TYPES.UNSUBSCRIBE, subscribeState);
    await this.removeDownlink();
  }

  sendSubscription(type: SUBSCRIBE_TYPES, subscribeState = this.subscribeState) {
    // https://iwiki.woa.com/pages/viewpage.action?pageId=1109917349#id-%E5%85%B7%E4%BD%93%E4%BF%A1%E4%BB%A4%E5%AE%9A%E4%B9%89-%E5%85%AD%E3%80%81unsubscribe
    let data: {
      audio?: boolean,
      bigVideo?: boolean,
      auxVideo?: boolean,
      smallVideo?: boolean,
      customData?: boolean,
      srcTinyId: string,
      srcUserId?: string;
    } = {
      srcTinyId: this.tinyId,
      srcUserId: this.userId
    };
    let command: string = SignalCommand.UNSUBSCRIBE;
    let responseCommand: string = SignalEvent.UNSUBSCRIBE_RESULT;
    if (type === SUBSCRIBE_TYPES.SUBSCRIBE_CHANGE) {
      data = {
        audio: subscribeState.audio,
        bigVideo: subscribeState.video,
        auxVideo: subscribeState.auxiliary,
        smallVideo: subscribeState.smallVideo,
        customData: subscribeState.datachannel,
        srcTinyId: this.tinyId
      };

      command = SignalCommand.SUBSCRIBE_CHANGE;
      responseCommand = SignalEvent.SUBSCRIBE_CHANGE_RESULT;
    }
    return this._signalChannel
      .sendWaitForResponseWithRetry({ command, data, responseCommand, timeout: 10000, retries: 3 })
      .then(({ data }: any) => {
        if (data.code !== 0) {
          const error = new RtcError({
            code: data.code,
            message: generateErrorMessage({
              key: ErrorName.ERROR_MESSAGE,
              data: { type, message: data.message }
            })
          });
          this._log.error(error);
          throw error;
        }
      });
  }

  getMainStreamVideoTrackId() {
    if (this.remoteVideoTrack && this.remoteVideoTrack.mediaTrack) {
      return this.remoteVideoTrack.mediaTrack.id;
    }
    return '';
  }

  getAuxStreamVideoTrackId() {
    if (this.remoteAuxiliaryTrack && this.remoteAuxiliaryTrack.mediaTrack) {
      return this.remoteAuxiliaryTrack.mediaTrack.id;
    }
    return '';
  }

  /**
   * update end2end delay
   */
  setDelay({ audioDelay, videoDelay }: { audioDelay: number; videoDelay: number; }) {
    this.remoteAudioTrack.stat.end2EndDelay = audioDelay;
    this.remoteVideoTrack.stat.end2EndDelay = videoDelay;
  }

  async onSinglePCReconnected() {
    // single pc 重连成功后，新的 peerConnection 还没有下行 ssrc 相关描述。
    // 因此需重新订阅下行，设置新的 ssrc，确保正常接收远端流
    if (this.ssrc.audio || this.ssrc.video || this.ssrc.auxiliary || this.isDataChannelSubscribed) {
      this._log.warn(`resubscribe ${JSON.stringify(this.subscribeState)}`);
      await this.doSubscribe(this.subscribeState);
      this.remoteAudioTrack.checkDecodeResult();
      this.remoteVideoTrack.checkDecodeResult();
      this.remoteAuxiliaryTrack.checkDecodeResult();
    }
  }

  get hasSSRC() {
    return this.ssrc.audio && this.ssrc.video && this.ssrc.auxiliary;
  }

  async doSubscribe(subscribeState = this.subscribeState, isNeedToGetNewSSRC = true) {
    if (!this.singlePC) return;
    this.singlePC.addDownlinkQueue.add(this.tinyId);

    await this.singlePC.waitForPeerConnectionConnected();
    try {
      if (isNeedToGetNewSSRC || !this.hasSSRC) {
        const ssrc = buildSSRCGroup();
        const { audioSsrc, bigVideoSsrc, bigVideoRtxSsrc, auxVideoSsrc, auxVideoRtxSsrc } = ssrc;
        this.ssrc = {
          audio: audioSsrc,
          video: bigVideoSsrc,
          videoRtx: bigVideoRtxSsrc,
          auxiliary: auxVideoSsrc,
          auxiliaryRtx: auxVideoRtxSsrc
        };
        this.singlePC.addDownlinkQueue.delete(this.tinyId);
        await this.singlePC.addDownlink({
          userId: this.userId,
          tinyId: this.tinyId,
          ssrc: this.ssrc,
          prevMids: this._prevMids
        });
        try {
          const result = await this._signalChannel.sendWaitForResponseWithRetry({
            command: SignalCommand.SPC_SUBSCRIBE,
            responseCommand: SignalEvent.SPC_SUBSCRIBE_RESULT,
            data: {
              srcUserId: this.userId,
              srcTinyId: this.tinyId,
              audio: subscribeState.audio,
              bigVideo: subscribeState.video,
              auxVideo: subscribeState.auxiliary,
              smallVideo: subscribeState.smallVideo,
              customData: subscribeState.datachannel ?? false,
              ssrc
            },
            retries: 3,
            retryTimeout: 0
          });
          // 为了避免 switchRoom 自动订阅失败, sdk 会补发一次订阅信令, -10036 表示后台已经自动订阅, sdk 忽略即可
          if (result.data.code !== 0 && result.data.code !== -10036) {
            throw new RtcError({ code: result.data.code, message: result.data.message });
          }
          this.isDataChannelSubscribed = subscribeState.datachannel ?? false;
        } catch (error) {
          await this.removeDownlink();
          throw error;
        }
        return;
      }
      this.singlePC.addDownlinkQueue.delete(this.tinyId);
      await this.singlePC.addDownlink({
        userId: this.userId,
        tinyId: this.tinyId,
        ssrc: this.ssrc,
        prevMids: this._prevMids
      });
    } finally {
      // jitterBuffer 设置（仅 media）
      const isDataChannelOnly = !subscribeState.audio && !subscribeState.video && !subscribeState.smallVideo && !subscribeState.auxiliary && subscribeState.datachannel;
      if (!isDataChannelOnly && IS_JITTER_BUFFER_TARGET_SUPPORTED) {
        const { main, aux } = this._room.jitterBufferDelay || {};
        const { jitterDelay = main, jitterDelayAux = aux } = this._room.scheduleResult.config || {};

        if (isNumber(jitterDelay) || isNumber(jitterDelayAux)) {
          this.setJitterBufferDelay({ mainDelay: jitterDelay, auxDelay: jitterDelayAux });
        }
      }
    }
  }

  async removeDownlink() {
    if (!this.singlePC) return;

    this.isDataChannelSubscribed = false;
    this.ssrc = {
      audio: 0,
      video: 0,
      videoRtx: 0,
      auxiliary: 0,
      auxiliaryRtx: 0
    };
    this.singlePC.removeDownlinkQueue.delete(this.tinyId);
    // 立即停止渐进式更新定时器，并 resolve 之前的 Promise
    clearTimeout(this.jitterBufferTimeoutId);
    if (this._jitterBufferResolve) {
      this._jitterBufferResolve();
      this._jitterBufferResolve = undefined;
    }
    // 重置 jitterBuffer 为 0，避免重新订阅时继承之前的延迟
    this.setJitterBufferDelay({ mainDelay: 0, auxDelay: 0 });
    // 保存返回的 mids，用于重新订阅时复用
    this._prevMids = await this.singlePC.removeDownlink(this.userId);
  }

  /**
   * 设置 jitterbuffer 大小。
   * 目前有两种设置方式：
   * 1. 使用浏览器提供的 jitterBufferTarget 设置。
   * 2. 后台配置 playoutDelay，优先级比浏览器接口高。
   */
  @addCallQueueValidArgs(() => 'jitter')
  setJitterBufferDelay({ mainDelay, auxDelay }: { mainDelay?: number; auxDelay?: number; }) {
    if (!IS_JITTER_BUFFER_TARGET_SUPPORTED || !this.singlePC || !this._peerConnection || (isUndefined(mainDelay) && isUndefined(auxDelay))) return Promise.resolve();
    this._log.info(`set jitterBuffer main: ${mainDelay} aux: ${auxDelay}`);
    const receivers = this.singlePC.getReceiversByUserId(this.userId);
    if (isNumber(mainDelay)) {
      this.remoteAudioTrack.jitterBufferDelay = mainDelay;
      this.remoteVideoTrack.jitterBufferDelay = mainDelay;
    }
    if (isNumber(auxDelay)) {
      this.remoteAuxiliaryTrack.jitterBufferDelay = auxDelay;
      // 只设置了辅流 jitterbuffer，则此时音频也设置为辅流 jitterbuffer 大小，辅流音频是混音到主流发送的。
      if (isUndefined(mainDelay)) {
        this.remoteAudioTrack.jitterBufferDelay = auxDelay;
      }
    }
    return new Promise(resolve => {
      this._jitterBufferResolve = resolve;
      this.doSetJitterBufferDelay({ mainDelay, auxDelay, receivers, resolve });
    });
  }

  doSetJitterBufferDelay({ mainDelay, auxDelay, receivers, resolve }: { mainDelay?: number; auxDelay?: number; receivers: RTCRtpReceiver[]; resolve: (arg?: AnalyserNode) => void; }) {
    try {
      if (mainDelay === 0 && auxDelay === 0) {
        // @ts-ignore
        receivers.forEach(receiver => receiver.jitterBufferTarget = 0);
        this._jitterBufferResolve = undefined;
        return resolve();
      }
      receivers.forEach(receiver => {
        const isAux = receiver.track === this.remoteAuxiliaryTrack.outMediaTrack || (isUndefined(mainDelay) && receiver.track === this.remoteAudioTrack.outMediaTrack);
        if ((isAux && isUndefined(auxDelay)) || (!isAux && isUndefined(mainDelay))) return;
        const targetDelay = isAux ? auxDelay || 0 : mainDelay;
        // @ts-ignore
        const value = (receiver.jitterBufferTarget || 0) + 100;
        if (value > targetDelay!) return;
        // @ts-ignore
        receiver.jitterBufferTarget = value;
        this._log.debug(`set ${isAux ? 'aux ' : ''}${receiver?.track?.kind} jitterBuffer delay ${value} -> ${targetDelay}`);
      });
      const isDone = !receivers.find(receiver => {
        const targetDelay = receiver.track === this.remoteAuxiliaryTrack.outMediaTrack ? auxDelay || 0 : mainDelay;
        // @ts-ignore
        return receiver.jitterBufferTarget < targetDelay;
      });
      if (isDone) {
        this._log.info(`set jitterBuffer main: ${mainDelay} aux: ${auxDelay} done`);
        this._jitterBufferResolve = undefined;
        return resolve();
      }
      this.jitterBufferTimeoutId = setTimeout(() => {
        this.doSetJitterBufferDelay({ mainDelay, auxDelay, receivers, resolve });
      }, 1000);
    } catch (error) {
      this._log.warn(`set jitterBuffer delay error: ${error}`);
      clearTimeout(this.jitterBufferTimeoutId);
      this._jitterBufferResolve = undefined;
      resolve();
    }
  }

  get audioReceiver() {
    return this.singlePC?.getReceiversByUserId(this.userId)[0] || null;
  }
  onDecodeFailed() {
    if (this._room.downlinkVideoCodec === VideoCodec.H265) {
      // H265 解码失败，通过 sendMediaSettings 同步后台
      this._room.requestRemoteFallbackToH264();
    }
  }
}

export default DownlinkConnectionSPC;
